import { ChevronDown, ChevronRight, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { Dispatch, memo, useMemo, useState } from "react";

import { Button } from "@buildpy/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@buildpy/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { safeCalcItem, safeCalcTrade } from "@buildpy/core/modules/quote/safe-calc";
import { formatWon } from "@buildpy/core/lib/format";
import { formatQty } from "@/modules/quote/ui/format";
import Money from "@/components/money";
import { flatRowsOf } from "@/modules/quote/ui/editor/mobile/mobile-item-sheet";
import TemplatePicker from "@/modules/quote/ui/editor/template-picker";
import { lockedNow, openUnlessLocked, unlessLockedEdit } from "@/modules/quote/ui/editor/locked-guard";
import { EditorAction, EditorItem, EditorTrade, ItemRef } from "@buildpy/core/modules/quote/editor/types";
import { TemplateItem } from "@buildpy/core/modules/template/types";
import { lockedDescribedBy } from "@/modules/quote/ui/editor/locked-banner";

// 줄 아래 작은 글 — 「3 ㎡ × 25,000」 · 상세 모드는 자재·노무를 나눠 적는다.
export const itemCaptionOf = (item: EditorItem, simple: boolean): string => {
  if (item.children.length > 0) return `하위 ${item.children.length}개 합계`;
  const price = item.material_unit_price + item.labor_unit_price;
  if (item.qty === 0 && price === 0) return "수량·단가 미입력";
  const qty = `${formatQty(item.qty)} ${item.unit}`.trim();
  if (simple) return `${qty} × ${formatWon(price)}`;
  const parts = [
    item.material_unit_price > 0 ? `자재 ${formatWon(item.material_unit_price)}` : "",
    item.labor_unit_price > 0 ? `노무 ${formatWon(item.labor_unit_price)}` : "",
  ].filter(Boolean);
  return `${qty} × ${parts.length > 0 ? parts.join(" · ") : formatWon(0)}`;
};

interface RowProps {
  item: EditorItem;
  child: boolean;
  simple: boolean;
  selected: boolean;
  onOpen: () => void;
}

const ItemLine = ({ item, child, simple, selected, onOpen }: RowProps) => {
  const amount = safeCalcItem(item).amount;
  const sets = item.children.length;
  return (
    <li>
      <Button
        aria-current={selected ? "true" : undefined}
        className="flex min-h-[3.25rem] w-full items-center justify-start text-left"
        size="row"
        variant="row"
        onClick={onOpen}>
        {/* 하위 줄 들여쓰기 — 버튼 px-3 에 pl-4 를 더해 옛 pl-7 자리 */}
        <span className={cn("flex min-w-0 flex-1 flex-col", child && "pl-4")}>
          <span className="flex min-w-0 items-center gap-x-1.5">
            {child && <span className="text-xs text-muted-foreground">└</span>}
            <span className={cn("truncate text-sm", item.item.trim() === "" && "text-muted-foreground")}>
              {item.item.trim() === "" ? "작업내역 없음" : item.item}
            </span>
            {sets > 0 && <span className="shrink-0 rounded-md bg-muted px-1 text-xs text-muted-foreground">세트 {sets}</span>}
          </span>
          <span className="truncate text-xs text-muted-foreground">{itemCaptionOf(item, simple)}</span>
        </span>
        <Money className="shrink-0 text-sm font-medium" value={amount} />
        <ChevronRight className="text-muted-foreground" />
      </Button>
    </li>
  );
};

interface Props {
  trade: EditorTrade;
  simple: boolean;
  dispatch: Dispatch<EditorAction>;
  templateItems: TemplateItem[];
  selectedKey: string | null;
  onOpenItem: (ref: ItemRef) => void;
  onAddItem: (tradeKey: string) => void;
  onEditTrade: (tradeKey: string) => void;
  // 잠긴 견적 — 추가·메뉴를 끈다(접기·항목 줄 열어 보기는 그대로).
  disabled?: boolean;
  // 편집기 lockedRef 를 그때 읽는 잠김 — 템플릿 「추가」 판정용.
  isLocked: () => boolean;
}

// 폰 공종 한 덩이 — 머리줄(접기·이름·항목 수·금액) · 펼치면 항목 줄 목록과 「+ 항목 · 템플릿에서 · ⋯」.
const MobileTrade = ({ trade, simple, dispatch: rawDispatch, templateItems, selectedKey, onOpenItem, onAddItem, onEditTrade, disabled = false, isLocked }: Props) => {
  // 템플릿 추가·삭제는 보내는 순간 잠겨 있으면 버린다(재렌더 전 잠김 포함) · 접기는 보낸다.
  const dispatch = useMemo(() => unlessLockedEdit(lockedNow(disabled, isLocked), rawDispatch), [disabled, isLocked, rawDispatch]);
  const amount = useMemo(() => safeCalcTrade({ group: trade.group, items: trade.items }).amount, [trade.group, trade.items]);
  const rows = useMemo(() => flatRowsOf(trade), [trade]);
  const collapsed = trade.collapsed;
  // 공종 메뉴 — 잠기면 열려 있어도 닫는다(포털이라 fieldset 이 못 끈다).
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div data-testid="phone-trade">
      <Button
        aria-expanded={!collapsed}
        className="flex min-h-12 w-full items-center justify-start text-left"
        size="row"
        variant="row"
        onClick={() => dispatch({ type: "TOGGLE_TRADE", tradeKey: trade.key })}>
        {collapsed ? <ChevronRight className="text-muted-foreground" /> : <ChevronDown className="text-muted-foreground" />}
        <span className="min-w-0 flex-1 truncate font-semibold">{trade.name}</span>
        <span className="shrink-0 text-xs font-normal text-muted-foreground">{trade.items.length}항목</span>
        <Money className="shrink-0 font-semibold" value={amount} />
      </Button>

      {!collapsed && (
        <>
          <ul className="flex flex-col divide-y divide-border border-t border-border">
            {rows.length === 0 && <li className="px-3 py-3 text-sm text-muted-foreground">항목이 없습니다.</li>}
            {rows.map(({ item, parentKey }) => (
              <ItemLine
                key={item.key}
                child={parentKey !== null}
                item={item}
                selected={selectedKey === item.key}
                simple={simple}
                onOpen={() => onOpenItem({ tradeKey: trade.key, itemKey: item.key, parentKey })}
              />
            ))}
          </ul>
          <fieldset aria-describedby={lockedDescribedBy(disabled)} className="flex min-w-0 items-center gap-2 border-t border-border px-3 py-2" disabled={disabled}>
            <Button className="flex-1" disabled={disabled} size="sm" variant="outline" onClick={() => onAddItem(trade.key)}>
              <Plus /> 항목
            </Button>
            <TemplatePicker
              disabled={disabled}
              isLocked={isLocked}
              items={templateItems}
              tradeName={trade.name}
              triggerFill
              triggerLabel="템플릿에서"
              onAdd={(items) => dispatch({ type: "ADD_TEMPLATE_ITEMS", tradeKey: trade.key, items })}
            />
            {/* 메뉴에서 시트를 여므로 비모달 — 모달이면 닫히며 body 의 pointer-events 가 남는다. */}
            <DropdownMenu modal={false} open={openUnlessLocked(menuOpen, disabled)} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild disabled={disabled}>
                <Button aria-label="공종 메뉴" className="size-9 shrink-0" size="icon" variant="ghost">
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => onEditTrade(trade.key)}>
                  <Pencil className="size-4" /> 공종명·조건 문구
                </DropdownMenuItem>
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => dispatch({ type: "REMOVE_TRADE", tradeKey: trade.key })}>
                  <Trash2 className="size-4" /> 공종 삭제
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </fieldset>
        </>
      )}
    </div>
  );
};

export default memo(MobileTrade);
